import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, Search, ExternalLink, LogOut } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getMainNavigationItems } from '@/data/navigationData';

interface UnifiedMobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
}

export const UnifiedMobileMenu = ({ isOpen, onClose, session }: UnifiedMobileMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Logged out successfully");
      navigate("/");
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const navigationItems = getMainNavigationItems(!!session);


  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] isolate">
      {/* Backdrop - solid background for proper visibility */}
      <div 
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      {/* Menu Panel */}
      <div className="fixed inset-y-0 right-0 z-[10000] w-full max-w-sm bg-background border-l border-border shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Menu</h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="h-8 w-8 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search tools and features..."
              className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              
              if (item.external) {
                return (
                  <a
                    key={item.id}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-foreground hover:bg-card-hover group"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-card-bg group-hover:bg-card-hover transition-colors">
                      <item.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium">{item.label}</span>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                      )}
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </a>
                );
              }
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                    isActive 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'text-foreground hover:bg-card-hover'
                  }`}
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-primary' 
                      : 'bg-card-bg group-hover:bg-card-hover'
                  }`}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium">{item.label}</span>
                    {item.description && !isActive && (
                      <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4">
          {session ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-card-bg rounded-lg">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold text-primary-foreground">
                    {session.user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {session.user.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {session.user.email}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-card-hover rounded-lg transition-all duration-200"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Link to="/auth" onClick={onClose}>
                <Button className="w-full py-3 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md">
                  Sign In
                </Button>
              </Link>
              <Link to="/auth" onClick={onClose}>
                <Button variant="outline" className="w-full py-3 border-border hover:bg-card-hover">
                  Sign Up
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );

};